import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, PartyPopper, MapPin, Lightbulb, CalendarClock, Library, ChevronDown, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { OrderAudioPanel } from "../components/mbox/OrderAudioPanel";
import { Logo } from "../components/Logo";

const fmt = (d) => (d && d !== "0000-00-00" ? d.slice(0, 10).split("-").reverse().join(".") : "–");

// Kundenseite (auch Handy): nur die laut Dispo heute laufenden Veranstaltungen mit Masten-Steuerung (Start/Schleife/Stopp, Timer).
// Kommende und vergangene Veranstaltungen liegen in "Durchsagen Verwaltung".
export default function EventPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [offen, setOffen] = useState({});   // order_pk -> aufgeklappt; laufende Events starten offen
  useEffect(() => {
    api.get("/mbox/my/orders").then(r => {
      setOrders(r.data);
      setOffen(Object.fromEntries((r.data || []).filter(o => o.active).map(o => [o.order_pk, true])));
    }).catch(() => setOrders([]));
  }, []);
  const toggle = (pk) => setOffen(o => ({ ...o, [pk]: !o[pk] }));

  const laufend = (orders || []).filter(o => o.active);

  const karte = (o) => (
    <section key={o.order_pk} className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 sm:p-5" data-testid={`event-order-${o.order_pk}`}>
      <button type="button" onClick={() => toggle(o.order_pk)} className="w-full min-w-0 text-left flex items-start gap-2" data-testid={`event-toggle-${o.order_pk}`}>
        {offen[o.order_pk] ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />}
        <span className="min-w-0 flex-1 block">
          <span className="text-sm font-bold text-gray-900 flex items-center gap-2 flex-wrap">
            <Lightbulb className="w-4 h-4 text-amber-500 shrink-0" /> {o.event || `Auftrag ${o.order_pk}`}
            {o.active ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium" data-testid={`event-active-${o.order_pk}`}>läuft</span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium flex items-center gap-1" data-testid={`event-upcoming-${o.order_pk}`}><CalendarClock className="w-3 h-3" /> ab {fmt(o.dispo_start)} bedienbar</span>
            )}
          </span>
          <span className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">{o.address || "–"}</span></span>
          <span className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><CalendarClock className="w-3 h-3 shrink-0" /> {fmt(o.dispo_start)} – {fmt(o.dispo_end)}</span>
        </span>
      </button>
      {offen[o.order_pk] && <div className="mt-4"><OrderAudioPanel orderPk={o.order_pk} /></div>}
    </section>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="event-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/hub")} className="text-gray-600 hover:text-fuchsia-600 px-2" data-testid="event-back-btn">
            <ArrowLeft className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Zurück</span>
          </Button>
          <div className="min-w-0">
            <h1 className="text-base font-bold text-gray-900 flex items-center gap-2"><PartyPopper className="w-5 h-5 text-fuchsia-600" /> Event</h1>
            <p className="text-xs text-gray-400 truncate">Laufende Veranstaltung – Durchsagen starten zeitgleich auf den gewählten Masten</p>
          </div>
          <div className="ml-auto hidden sm:block"><Logo size="small" /></div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-5 sm:space-y-6">
        <button type="button" onClick={() => navigate("/durchsagen")}
          className="w-full flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3 text-left hover:border-fuchsia-300 transition" data-testid="event-library-link">
          <span className="w-9 h-9 rounded-lg bg-fuchsia-100 flex items-center justify-center shrink-0"><Library className="w-4 h-4 text-fuchsia-600" /></span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-gray-900">Durchsagen Verwaltung</span>
            <span className="block text-xs text-gray-400">Durchsagen speichern, vorhören und mit Events verknüpfen</span>
          </span>
        </button>

        {orders === null ? <p className="text-sm text-gray-500">Lade Veranstaltungen…</p> : laufend.length ? laufend.map(karte) : (
          <div className="bg-white border border-gray-200 rounded-xl p-6 text-sm text-gray-500" data-testid="event-empty">
            Heute läuft keine Veranstaltung – die Masten sind nur von Lieferung bis Abholung bedienbar. Kommende und vergangene Veranstaltungen finden Sie unter <button type="button" onClick={() => navigate("/durchsagen")} className="text-fuchsia-700 underline font-medium">Durchsagen Verwaltung</button>.
          </div>
        )}
      </main>
    </div>
  );
}
